import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
	defaultOrderBy,
	foldersOnTopOrderedBy,
} from "../utilities/tableSortUtils";
import { useDispatch, useSelector } from "react-redux";
import { Radio, Group } from "@mantine/core";
import SelectTable from "./Tables/SelectTable";
import { DropDownButtonWithRouter } from "./DropDownButton";
import { LexWorkplaceGavelIcon } from "./LexWorkplaceIcons";
import InteractiveTableCell from "./Tables/InteractiveTableCell";
import DocumentSlidingPane from "./DocumentPreviewRightPane";
import useDocumentPreviewPane from "./useDocumentPreviewPane";
import { IconEye, IconLink } from "@tabler/icons-react";
import { listNonNormalItems } from "../actions/archiveActions";
import {
	copyDocLinkToClipboard,
	fetchBrowseDirectory,
	getSignedUrlForDocumentAndDownload,
	showRenameDocumentDialog,
} from "../actions/documentActions";
import { showEditFolderDialog } from "../actions/folderActions";
import {
	ARCHIVE_LIFECYCLE_STATUS,
	NORMAL_LIFECYCLE_STATUS,
	TRASH_LIFECYCLE_STATUS,
	getDisabledStateFromLifecycleStatuses,
} from "../utilities/lifecycleStatus";
import { encodeFullpath, iconMap } from "../utilities/utilities";
import { useArchiveMatterContentsAccess } from "../utilities/archiveAccess";
import {
	setAllCheckboxes,
	restoreFromArchiveMultiselectOnArchivePage,
	restoreFromTrashMultiselectOnArchivePage,
	deleteMultiselectOnArchivePage,
	archiveMultiselectOnArchivePage,
} from "../actions/multiselectActions";
import {
	selectTableRootMap,
	selectTableTypeMap,
} from "../reducers/multiselectReducer";
import { ID_COLUMN_WIDTH, MAIN_COLUMN_WIDTH } from "./Tables/SortableTable";

const firmDocsItemTypeOptions = [{ value: "docs", label: "Documents" }];
const matterItemTypeOptions = [
	...firmDocsItemTypeOptions,
	{ value: "email", label: "Email" },
	{ value: "notes", label: "Notes" },
];

const IdCell = (row) => (
	<div style={{ textAlign: "left" }} data-id={row.value}>
		{row.original.type === "Folder" ? "" : row.value}
	</div>
);

function buildTypeRadios({ options, selectedType, onTypeChange }) {
	return (
		<div style={{ display: "flex", alignItems: "center", marginLeft: 16 }}>
			<div>Viewing:</div>
			<Radio.Group
				value={selectedType?.value}
				onChange={(value) => onTypeChange(null, { value })}
				style={{ marginLeft: 16 }}
			>
				<Group gap="lg">
					{options.map((opt) => (
						<Radio
							key={opt.value}
							value={opt.value}
							label={opt.label}
						/>
					))}
				</Group>
			</Radio.Group>
		</div>
	);
}

export default function ArchiveAndTrashList({ root }) {
	// root: "archive" | "trash"
	// id: matter ID (matters route only)
	// namespace: "public" | "private" (firmDocs route only)
	// context: "docs" | "email" | "notes" (firmDocs: "docs" only)
	const { id, context, namespace } = useParams();
	const isFirmDocs = namespace !== undefined;
	const matterID = isFirmDocs ? 0 : id;
	const basepath = namespace === "public" ? "public/" : "";

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const items = useSelector((state) => state.archive.items);
	const loading = useSelector((state) => state.archive.loading);
	const listStale = useSelector((state) => state.archive.listStale);
	const foldersOnTop = useSelector(
		(state) => state.userSettings.foldersOnTop
	);

	// use the matter details (fetched by MatterDetail on these tabs), not the last directory listing
	// firm docs has no matter, so it is always normal
	const matterLifecycleStatus = useSelector((state) =>
		isFirmDocs
			? NORMAL_LIFECYCLE_STATUS
			: state.matter.matterDetails.matterLifecycleStatus
	);
	const matterLifecycleStatusObject = getDisabledStateFromLifecycleStatuses({
		matterLifecycleStatus,
	});

	const archiveAccess = useArchiveMatterContentsAccess();
	const documentPreviewPane = useDocumentPreviewPane();

	const tableRoot = `${root}/${context}/`;
	const selectedItems = useSelector(
		(state) => state.multiselect[selectTableRootMap[tableRoot]]
	);
	const selectedCount = selectedItems
		? Object.keys(selectedItems.Object).length +
			Object.keys(selectedItems.Folder).length
		: 0;

	const [itemsMatchingFilter, setItemsMatchingFilter] = useState([]);

	const itemTypeOptions = isFirmDocs
		? firmDocsItemTypeOptions
		: matterItemTypeOptions;
	const selectedType = itemTypeOptions.find((o) => o.value === context);

	function refreshList() {
		dispatch(
			listNonNormalItems({
				matterID,
				basepath,
				lifecycleStatusToGet: root,
				itemType: context,
			})
		);
	}

	useEffect(refreshList, [matterID, basepath, root, context]);

	function renameItem(item) {
		// The rename dialogs check for duplicate names against the item's
		// siblings, which this list doesn't have, so load them first
		fetchBrowseDirectory(matterID, item.apiBasepath).then(({ items }) => {
			const dialogArgs = {
				matterID,
				currentName: item.filename,
				apiBasepath: item.apiBasepath,
				currentLocationFilesAndFolders: items,
				onSuccess: refreshList,
			};
			dispatch(
				item.type === "Folder"
					? showEditFolderDialog({ ...dialogArgs, folderID: item.id })
					: showRenameDocumentDialog({
							...dialogArgs,
							documentID: item.id,
						})
			);
		});
	}

	function onTypeChange(_name, option) {
		if (isFirmDocs) {
			navigate(`/firmDocs/${namespace}/${root}/${option.value}`);
		} else {
			navigate(`/matters/${id}/${root}/${option.value}`);
		}
	}

	function headerBoxClicked() {
		var config = { Object: {}, Folder: {} };
		const source = itemsMatchingFilter.length ? itemsMatchingFilter : items;
		source.forEach((item) => {
			const originalItem = item._original ? item._original : item;
			config[selectTableTypeMap[originalItem.type]][originalItem.id] = {
				...originalItem,
			};
		});
		dispatch(setAllCheckboxes(selectTableRootMap[tableRoot], config));
	}

	function getActionMenuItems(openCsvExport) {
		const countLabel = selectedCount ? ` (${selectedCount})` : "";
		const baseDisabled =
			selectedCount === 0 || matterLifecycleStatusObject.disabled;
		const baseDisabledText = matterLifecycleStatusObject.disabled
			? matterLifecycleStatusObject.disabledText
			: undefined;

		if (root === "archive") {
			const unarchiveAccess = archiveAccess.unarchive;
			return [
				{
					text: `Unarchive${countLabel}`,
					onSelect: () =>
						dispatch(
							restoreFromArchiveMultiselectOnArchivePage({
								selectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: baseDisabled || unarchiveAccess.disabled,
					disabledHoverText: unarchiveAccess.disabled
						? unarchiveAccess.disabledText
						: baseDisabledText,
				},
				{
					text: `Move to Trash${countLabel}`,
					onSelect: () =>
						dispatch(
							deleteMultiselectOnArchivePage({
								selectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: baseDisabled || unarchiveAccess.disabled,
					disabledHoverText: unarchiveAccess.disabled
						? unarchiveAccess.disabledText
						: baseDisabledText,
				},
				{ divider: true },
				{ text: "Export Data as CSV", onSelect: openCsvExport },
			];
		} else {
			const archiveBranchAccess = archiveAccess.archive;
			return [
				{
					text: `Restore from Trash${countLabel}`,
					onSelect: () =>
						dispatch(
							restoreFromTrashMultiselectOnArchivePage({
								selectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: baseDisabled,
					disabledHoverText: baseDisabledText,
				},
				{
					text: `Archive${countLabel}`,
					onSelect: () =>
						dispatch(
							archiveMultiselectOnArchivePage({
								selectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: baseDisabled || archiveBranchAccess.disabled,
					disabledHoverText: archiveBranchAccess.disabled
						? archiveBranchAccess.disabledText
						: baseDisabledText,
				},
				{ divider: true },
				{ text: "Export Data as CSV", onSelect: openCsvExport },
			];
		}
	}

	function getRowMenuItems(row) {
		const singleItemSelectedItems = {
			Object:
				selectTableTypeMap[row.original.type] === "Object"
					? { [row.original.id]: row.original }
					: {},
			Folder:
				selectTableTypeMap[row.original.type] === "Folder"
					? { [row.original.id]: row.original }
					: {},
		};
		const rowDisabled = matterLifecycleStatusObject.disabled;
		const rowDisabledHoverText = matterLifecycleStatusObject.disabledText;

		const { id, type } = row.original;
		const renameMenuItem = {
			text: "Rename",
			onSelect: () => renameItem(row.original),
			disabled: rowDisabled,
			disabledHoverText: rowDisabledHoverText,
		};
		// Same item actions as the home tab offers for archived/trashed items
		const itemActionMenuItems =
			type === "File"
				? [
						{
							text: "Document Properties",
							href: "/docs/" + id,
						},
						{
							text: "Copy Internal Document URL",
							onSelect: () =>
								dispatch(copyDocLinkToClipboard(id)),
						},
						{ divider: true },
						renameMenuItem,
						{
							text: "Download a Copy",
							onSelect: () =>
								dispatch(
									getSignedUrlForDocumentAndDownload(id)
								),
						},
						{ divider: true },
					]
				: type === "Folder"
					? [renameMenuItem, { divider: true }]
					: [];

		if (root === "archive") {
			const unarchiveAccess = archiveAccess.unarchive;
			return [
				...itemActionMenuItems,
				{
					text: "Restore from Archive",
					onSelect: () =>
						dispatch(
							restoreFromArchiveMultiselectOnArchivePage({
								selectedItems: singleItemSelectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: rowDisabled || unarchiveAccess.disabled,
					disabledHoverText: unarchiveAccess.disabled
						? unarchiveAccess.disabledText
						: rowDisabledHoverText,
				},
				{
					text: "Move to Trash",
					onSelect: () =>
						dispatch(
							deleteMultiselectOnArchivePage({
								selectedItems: singleItemSelectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: rowDisabled || unarchiveAccess.disabled,
					disabledHoverText: unarchiveAccess.disabled
						? unarchiveAccess.disabledText
						: rowDisabledHoverText,
				},
			];
		} else {
			const archiveBranchAccess = archiveAccess.archive;
			return [
				...itemActionMenuItems,
				{
					text: "Restore from Trash",
					onSelect: () =>
						dispatch(
							restoreFromTrashMultiselectOnArchivePage({
								selectedItems: singleItemSelectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: rowDisabled,
					disabledHoverText: rowDisabledHoverText,
				},
				{
					text: "Archive",
					onSelect: () =>
						dispatch(
							archiveMultiselectOnArchivePage({
								selectedItems: singleItemSelectedItems,
								matterID,
								basepath,
								itemType: context,
							})
						),
					disabled: rowDisabled || archiveBranchAccess.disabled,
					disabledHoverText: archiveBranchAccess.disabled
						? archiveBranchAccess.disabledText
						: rowDisabledHoverText,
				},
			];
		}
	}

	function getRowButtons(row) {
		if (row.original.type !== "File") {
			return undefined;
		}
		return [
			{
				iconComponent: <IconLink size={20} />,
				onClick: () =>
					dispatch(copyDocLinkToClipboard(row.original.id)),
				title: "Copy Internal Document URL",
			},
			{
				iconComponent: <IconEye size={20} />,
				title: "Preview This Document",
				onClick: () =>
					documentPreviewPane.openItemPreview({
						type: "File",
						id: row.original.id,
						title: row.original.filename,
						rowData: {
							...row.original,
							documentLifecycleStatus:
								root === "archive"
									? ARCHIVE_LIFECYCLE_STATUS
									: TRASH_LIFECYCLE_STATUS,
							matterOrFolderLifecycleStatusObject:
								matterLifecycleStatusObject,
						},
					}),
			},
		];
	}

	const columns = useMemo(
		() => [
			{
				Header: "ID",
				accessor: "id",
				width: ID_COLUMN_WIDTH,
				Cell: IdCell,
			},
			{
				Header: "Name",
				accessor: "filename",
				width: MAIN_COLUMN_WIDTH,
				wildcard: true,
				unhidable: true,
				Cell: (row) => (
					<InteractiveTableCell
						menuItems={getRowMenuItems(row)}
						otherButtons={getRowButtons(row)}
						iconComponent={iconMap(row)?.iconComponent}
					>
						{row.original.type === "Folder" ? (
							// browse into the folder from its home tab
							<Link
								to={encodeFullpath(
									row.original.folderPath + row.value + "/"
								)}
							>
								{row.value}
							</Link>
						) : (
							row.value
						)}
					</InteractiveTableCell>
				),
			},
			{
				Header: "Location",
				accessor: "displayFolderPath",
				width: 250,
				Cell: (row) => (
					<Link to={row.original.folderPath}>{row.value}</Link>
				),
			},
		],
		[root, matterID, basepath, context, matterLifecycleStatusObject]
	);

	const lifecycleLabel = root === "archive" ? "Archive" : "Trash";

	return (
		<div className="fullHeightFlex">
			<SelectTable
				tableUniqueID={`/${root}/${context}`}
				root={tableRoot}
				columns={columns}
				data={items}
				loading={loading}
				dataIsStale={listStale}
				filterable
				orderByMethod={
					foldersOnTop ? foldersOnTopOrderedBy : defaultOrderBy
				}
				noResultsText={`No ${selectedType.label} in the ${lifecycleLabel}`}
				leftSideFilterSpace={buildTypeRadios({
					options: itemTypeOptions,
					selectedType,
					onTypeChange,
				})}
				headerBoxClicked={headerBoxClicked}
				actionButtons={({ openCsvExport }) => (
					<DropDownButtonWithRouter
						title="Actions"
						iconComponent={<LexWorkplaceGavelIcon />}
						pullRight
						menuItems={getActionMenuItems(openCsvExport)}
					/>
				)}
				onFilteredChange={(filteredColumns) => {
					if (!filteredColumns || !filteredColumns.length) {
						setItemsMatchingFilter([]);
					}
				}}
				filteringCompleteCallback={(
					filteredData,
					filterId,
					activeFilters
				) => {
					if (activeFilters.length === 0) {
						setItemsMatchingFilter([]);
					} else {
						const lastActiveFilterId =
							activeFilters.slice(-1)[0].id;
						if (activeFilters.length === 1) {
							setItemsMatchingFilter(filteredData);
						} else if (lastActiveFilterId === filterId) {
							setItemsMatchingFilter(filteredData);
						}
					}
				}}
			/>
			<DocumentSlidingPane
				openPane={documentPreviewPane.previewOpen}
				selectedRowData={documentPreviewPane.previewSelectedRow ?? {}}
				closeRightPane={documentPreviewPane.closePreview}
				matterID={matterID}
			/>
		</div>
	);
}
